import { AppRoutes } from "@/shared/lib/routes"
import { createRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { rootRoute } from "./__root"
import { useQuizStore } from "@/stores/quiz-store"
import { QuizRenderer, resolveQuiz, getRequestedQuizKey, withQuizIntro } from "@/features/quiz"
import { useConfig } from "@/features/config"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { cn } from "@/lib/utils"

export const quizRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.QUIZ,
  component: QuizPage,
})

function QuizPage() {
  const navigate = useNavigateWithParams()
  // Effective params merge the funnel config with live URL params (URL wins),
  // recomputed once the config lands so the resolved quiz updates with it.
  const { params } = useConfig()
  const effectiveParams = useMemo(() => {
    const merged: Record<string, string> = { ...params }
    if (typeof window !== "undefined") {
      new URLSearchParams(window.location.search).forEach((value, key) => {
        merged[key] = value
      })
    }
    return merged
  }, [params])

  const resolved = useMemo(() => resolveQuiz(effectiveParams), [effectiveParams])
  const { id, fellBack } = resolved
  const config = useMemo(
    () => withQuizIntro(resolved.config, effectiveParams),
    [resolved.config, effectiveParams],
  )

  const startQuiz = useQuizStore((s) => s.startQuiz)
  const completed = useQuizStore((s) => s.completed)

  // Activate the resolved quiz; clears stale answers only when it actually changes.
  useEffect(() => {
    startQuiz(id)
  }, [id, startQuiz])

  useEffect(() => {
    if (fellBack) {
      trackEvent(ANALYTICS_EVENTS.QUIZ_CONFIG_MISSING, {
        metadata: { requested: getRequestedQuizKey(effectiveParams) },
      })
    }
  }, [fellBack, effectiveParams])

  useEffect(() => {
    if (completed) {
      navigate({ to: AppRoutes.CHECKOUT })
    }
  }, [completed, navigate])

  const handleComplete = () => {
    navigate({ to: AppRoutes.CHECKOUT })
  }

  return (
    <div className="min-h-[100dvh] bg-dark-base">
      <div className={cn("mx-auto w-full", id === "aura-scan" ? "max-w-[500px]" : "max-w-app")}>
        <QuizRenderer config={config} onComplete={handleComplete} />
      </div>
    </div>
  )
}
